import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { Shield, Lock, Banknote, ArrowRight, Check, Receipt, Flame, MapPin } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1742522450616-a2cf0cba1274";
const VAULT = "https://images.unsplash.com/photo-1603731487980-a5b7b59cef69";

export default function Landing() {
  return (
    <div className="min-h-screen bg-cream text-ink">
      <AppHeader variant="marketing" />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7" data-testid="hero">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-linen border border-sand mb-8">
              <span className="text-xs font-sans uppercase tracking-[0.2em] font-semibold text-forest">Your money, levelled up</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              Your money,<br />
              <span className="italic text-forest">levelled up.</span>
            </h1>
            <p className="mt-8 text-lg text-moss max-w-xl leading-relaxed">
              Withdraw Guard is the lifestyle bank account that pays your bills before you can spend them,
              seals your savings until the day you choose, celebrates every on-time streak — and lights up
              the dealerships, spas, salons and getaways worth levelling up for, right on your map.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <Link to="/register"
                className="inline-flex items-center gap-2 bg-forest text-white rounded-lg px-7 py-4 text-base font-medium hover:bg-forest-deep transition-colors"
                data-testid="hero-cta">
                Preorder for $45 · explore the demo
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <a href="#how" className="text-moss hover:text-ink text-sm tracking-wide" data-testid="hero-learn">
                See how a level-up works →
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] font-semibold text-sage">
              Founding Member preorder · $45/month · your $45 today is your first month
            </p>
          </div>

          <div className="lg:col-span-5 relative" data-testid="hero-image">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-sand">
              <img src={HERO} alt="Levelling up your lifestyle" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-cream border border-sand rounded-xl p-5 max-w-[240px]" data-testid="hero-streak-card">
              <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-clay"><Flame className="w-3 h-3" strokeWidth={2} /> Phone bill</div>
              <div className="font-mono text-2xl text-ink mt-1 whitespace-nowrap">3-month streak</div>
              <div className="text-xs text-moss mt-1">paid on time, sealed automatically</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section id="how" className="border-t border-sand bg-linen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-4">The four pillars</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Lock it. Pay it. Streak it. Live it.</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Lock, color: "rgb(var(--c-clay))", t: "Locks", d: "Untouchable Retirement until 60, Date Time Locks for the wedding or the down payment, an Emergency vault that opens only on verified job loss. Money you can't sabotage.",
                pts: ["No early withdrawals, no exceptions", "Extend a lock any time — never shorten it", "Every lock is yours, on your timeline"], testid: "pillar-locks" },
              { Icon: Receipt, color: "rgb(var(--c-gold))", t: "Bill Locks", d: "Name a bill and its due date. Spending and every incoming deposit are swept into a sealed lock — soonest bill first — then handed back the day before it's due.",
                pts: ["Bills pay themselves first", "One-tap release on unlock day", "Shown on your Payday Calendar"], testid: "pillar-bill-locks" },
              { Icon: Flame, color: "rgb(var(--c-forest))", t: "Streaks", d: "Money Progress groups your locks by what they're for and tells the story: \"Saved $180 for phone bill\" → \"Paid ✓\" → a 3-month streak badge.",
                pts: ["Auto-detected savings stories", "On-time streak badges", "Card spending by category"], testid: "pillar-streaks" },
              { Icon: MapPin, color: "rgb(var(--c-ink))", t: "Your City Map", d: "Drop in your address and see the car dealerships, used-car lots, paint & body shops, spas, salons, casinos and airports within 15 miles — the level-up, mapped.",
                pts: ["Seven lifestyle categories", "Saved address, instant reload", "Phone, hours and website on every pin"], testid: "pillar-map" },
            ].map((c) => (
              <div key={c.t} className="bg-cream border border-sand rounded-2xl p-8" data-testid={c.testid}>
                <c.Icon className="w-7 h-7" style={{ color: c.color }} strokeWidth={1.5} />
                <h3 className="font-serif text-3xl tracking-tight mt-6">{c.t}</h3>
                <p className="text-moss mt-3 leading-relaxed">{c.d}</p>
                <ul className="mt-8 space-y-3">
                  {c.pts.map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-ink">
                      <Check className="w-4 h-4 text-forest mt-0.5" strokeWidth={2} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-sand">
              <img src={VAULT} alt="Discipline on autopilot" className="w-full h-[500px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-4">A level-up, step by step</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Discipline on autopilot.</h2>

            <ol className="mt-10 space-y-8">
              {[
                { n: "01", t: "Payday lands", d: "Your paycheck hits Spending. Retirement, car and custom commitments pull on schedule — delay any of them with a date when life happens." },
                { n: "02", t: "Bills seal themselves", d: "Bill Locks sweep what's left into sealed locks, soonest due date first, until every upcoming bill is covered." },
                { n: "03", t: "Release day", d: "The day before it's due, a banner prompts you: release the money, pay the bill, keep the streak alive." },
                { n: "04", t: "Level up", d: "Money Progress turns it into a story. Your Map shows what's next — the car, the spa day, the flight." },
              ].map((s) => (
                <li key={s.n} className="flex gap-6">
                  <div className="font-mono text-sm text-sage pt-1">{s.n}</div>
                  <div>
                    <div className="font-sans text-xl font-medium text-ink">{s.t}</div>
                    <div className="text-moss mt-1.5 leading-relaxed">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="more-features">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-4">Everything in one account</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">A whole bank, built around levelling up.</h2>
          <p className="text-moss mt-4 leading-relaxed">
            A real Spending account and debit card, payday commitments, a payday calendar,
            custom savings goals, a city map and a net-worth tracker — all wired together through
            your Your money, levelled up.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { t: "Real Spending account", d: "Routing and account numbers at our Your money, levelled up. Direct-deposit your paycheck straight in." },
            { t: "Debit card with controls", d: "A real card, Apple Pay-ready, with merchant-category rules you set. Track the plastic from print to mailbox." },
            { t: "Bill Locks", d: "Spending and new deposits sweep into sealed locks for upcoming bills, soonest due first, released the day before." },
            { t: "Payday commitments", d: "Retirement, rainy-day, car emergency and baby deposits pull on payday. Delay any one — or all — with a date." },
            { t: "Payday Calendar", d: "Every pull, every bill due day and every bill-lock unlock on one month grid." },
            { t: "Money Progress + streaks", d: "Your locks grouped into stories, on-time streak badges, and card spending by category at the top of Banking." },
            { t: "Your City Map", d: "Dealerships, used-car lots, paint & body shops, spas, salons, casinos and airports within 15 miles of home." },
            { t: "Custom savings goals", d: "Boat, home, car, wedding — each goal is its own sealed vault that releases the day you hit the number." },
            { t: "Bank-grade 2FA", d: "TOTP authenticator + 10 single-use recovery codes. Login lockout after 5 wrong attempts." },
          ].map((c) => (
            <div key={c.t} className="bg-linen border border-sand rounded-xl p-6">
              <Check className="w-4 h-4 text-forest" strokeWidth={2} />
              <div className="font-serif text-xl mt-3 text-ink">{c.t}</div>
              <div className="text-sm text-moss mt-2 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-sand bg-ink text-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-6">Membership</div>
          <h2 className="font-serif text-5xl sm:text-6xl tracking-tight" data-testid="pricing-headline">$45 a month.</h2>
          <p className="text-lg text-sand mt-6 max-w-2xl mx-auto leading-relaxed" data-testid="pricing-copy">
            $45 monthly to level up your life — travel more, save without thinking, and spend with peace of mind.
            Every vault, bill lock, map and career card. No other fees, no upsells, cancel any time.
          </p>
          <Link to="/register"
            className="mt-10 inline-flex items-center gap-2 bg-cream text-ink rounded-lg px-8 py-4 text-base font-medium hover:bg-white transition-colors"
            data-testid="pricing-cta">
            Start for $45/month
            <Banknote className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-sand py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-moss">
            <Shield className="w-4 h-4" strokeWidth={1.5} />
            <span className="font-serif text-base">Withdraw Guard</span>
            <span className="text-xs">· $45/month · cancel any time</span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-xs uppercase tracking-[0.2em] font-semibold text-moss hover:text-ink transition-colors"
              data-testid="footer-privacy-link">
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-xs uppercase tracking-[0.2em] font-semibold text-moss hover:text-ink transition-colors"
              data-testid="footer-terms-link">
              Terms
            </Link>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">
              © Withdraw Guard
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
